import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { deleteFileFromBrowserStorage, saveFileToBrowserStorage, TrackerVideoEvent, useGeneratePosterFromVideoAttachment } from '@bonusly/common-action-input';
import { Link, useToast } from '@bonusly/design-system';
import { useAnalytics, useFeatureFlag } from '@bonusly/who-am-i';

import { isFirefox } from '../../../../utils/browser';
import { setupFirefoxMediaRecorder } from '../utils/firefox-media-recorder';
import { checkMediaRecorderSupport, getExtensionFromRecorderMimeType, getSupportedMimeType } from '../utils/media-recorder';
import { validateVideo } from '../utils/video-validation';
import { useVideoTranscription } from './use-video-transcription';
import { useViewportAspectRatio } from './use-viewport-aspect-ratio';

const COUNTDOWN_BASE_URL = 'https://recognition-production-assets-cdn.s3.us-east-1.amazonaws.com/video';

interface Device {
	deviceId: string;
	label: string;
}

interface VideoRecorderState {
	isRecording: boolean;
	recordedVideo: string;
	stream: MediaStream | null;
	originalStream: MediaStream | null;
	recordedPreviewReady: boolean;
	mediaRecorder: MediaRecorder | null;
	devices: { video: Device[]; audio: Device[] };
	countdown: number | null | '';
	recordingAttempts: number;
	videoPoster: string | null;
	isTranscribing: boolean;
}

export const useVideoRecorder = (
	videoRef: React.RefObject<HTMLVideoElement>,
	recordedVideoRef: React.RefObject<HTMLVideoElement>,
	isOpen: boolean,
	isMobile?: boolean
) => {
	const [state, setState] = useState<VideoRecorderState>({
		isRecording: false,
		recordedVideo: '',
		stream: null,
		originalStream: null,
		recordedPreviewReady: false,
		mediaRecorder: null,
		devices: { video: [], audio: [] },
		countdown: null,
		recordingAttempts: 0,
		videoPoster: null,
		isTranscribing: false,
	});

	const obtainedDevicePermissionRef = useRef(false);
	const selectedVideoDeviceRef = useRef<string | undefined>(undefined);
	const selectedAudioDeviceRef = useRef<string | undefined>(undefined);
	const skipVideoProcessingRef = useRef(false); // User restarts we want to skip video processing
	const chunks = useRef<Blob[]>([]);
	const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const previousStreamRef = useRef<MediaStream | null>(null);
	const previousOriginalStreamRef = useRef<MediaStream | null>(null);
	const { track } = useAnalytics();
	const toast = useToast();
	const { t } = useTranslation('video');
	const generatePoster = useGeneratePosterFromVideoAttachment();
	// Add audio cache ref
	const countdownAudioCache = useRef<Record<number, HTMLAudioElement>>({});
	const firefoxMediaRecorderCleanupRef = useRef<(() => void) | null>(null);
	const mobileVideoFeatureFlag = useFeatureFlag('mobile-video');
	const { startTranscription, stopTranscription, isTranscribing, getSuggestedBonusReason, isLoadingSuggestedReason, getSpeechTags, clearTranscriptText } =
		useVideoTranscription(state.stream);

	// Update transcription state when it changes
	useEffect(() => {
		setState((prev) => ({ ...prev, isTranscribing }));
	}, [isTranscribing]);

	// Stops the media recorder. stop() is wired to call
	// handleRecordingStop() which stops the media recorder
	const stopRecording = () => {
		if (!state.mediaRecorder || !state.stream) return;

		// Stop transcription when recording stops
		if (mobileVideoFeatureFlag && isTranscribing) stopTranscription();

		if (isFirefox) {
			// Store the cleanup function
			firefoxMediaRecorderCleanupRef.current = setupFirefoxMediaRecorder(state.mediaRecorder, state.stream, {
				onStop: handleRecordingStop,
			}).cleanup;
		} else {
			// For non-Firefox browsers, set up the onstop handler directly
			state.mediaRecorder.onstop = async () => {
				if (!state.stream || !state.mediaRecorder) return;
				const videoTrack = state.stream.getVideoTracks()[0];
				const audioTrack = state.stream.getAudioTracks()[0];
				await handleRecordingStop(state.mediaRecorder, videoTrack, audioTrack);
			};
		}

		state.mediaRecorder.stop();
	};

	const clearCountdown = () => {
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
		setState((prev) => ({ ...prev, countdown: null }));
	};

	const getUserMediaDevices = async () => {
		const deviceList = await navigator.mediaDevices.enumerateDevices();
		setState((prev) => ({
			...prev,
			devices: {
				video: deviceList.filter((d) => d.kind === 'videoinput').map((d) => ({ deviceId: d.deviceId, label: d.label })),
				audio: deviceList.filter((d) => d.kind === 'audioinput').map((d) => ({ deviceId: d.deviceId, label: d.label })),
			},
		}));
	};

	const loadSavedDevices = useCallback(async () => {
		try {
			const storedData = localStorage.getItem('video-audio-recording-preferences');
			if (storedData) {
				const { videoDeviceId, audioDeviceId } = JSON.parse(storedData);

				// Get current available devices
				const deviceList = await navigator.mediaDevices.enumerateDevices();
				const availableVideoDevices = deviceList.filter((d) => d.kind === 'videoinput');
				const availableAudioDevices = deviceList.filter((d) => d.kind === 'audioinput');

				// Only use saved devices if they still exist
				if (videoDeviceId && availableVideoDevices.some((d) => d.deviceId === videoDeviceId)) {
					selectedVideoDeviceRef.current = videoDeviceId;
				}
				if (audioDeviceId && availableAudioDevices.some((d) => d.deviceId === audioDeviceId)) {
					selectedAudioDeviceRef.current = audioDeviceId;
				}
			}
		} catch (error) {
			console.error('Error loading saved devices:', error);
		}
	}, []);

	const saveDevicePreferences = useCallback((videoDeviceId?: string, audioDeviceId?: string) => {
		try {
			const videoId = videoDeviceId ?? selectedVideoDeviceRef.current;
			const audioId = audioDeviceId ?? selectedAudioDeviceRef.current;

			if (videoId || audioId) {
				const devicePreferences = {
					videoDeviceId: videoId,
					audioDeviceId: audioId,
				};
				localStorage.setItem('video-audio-recording-preferences', JSON.stringify(devicePreferences));
			}
		} catch (error) {
			console.error('Error saving device preferences:', error);
		}
	}, []);
	const aspectRatio = useViewportAspectRatio();

	const initializeMediaStream = useCallback(async () => {
		try {
			setState((prev) => ({ ...prev, recordedVideo: '' }));
			// Defines default video recordig parameters condusive to portrait
			// orientation on desktop cameras.
			const desktopDefaultConstraints = {
				width: { ideal: 1280 }, // Desktop landscape width
				height: { ideal: 720 }, // Desktop landscape height
				aspectRatio: { ideal: 16 / 9 }, // Desktop landscape aspect ratio
			};
			// Defines default video recordig parameters condusive to portrait
			// dimensions for landscape-oriented mobile dimensions, be they
			// on a desktop browser or mobile device.
			const mobileDefaultConstraints = {
				width: { ideal: aspectRatio.idealWidth },
				height: { ideal: aspectRatio.idealHeight },
				aspectRatio: { ideal: aspectRatio.aspectRatio },
				facingMode: 'user',
			};

			// Load saved devices and verify they still exist
			await loadSavedDevices();

			// Ok, now which constraints are relevant?
			const constraints = {
				video: selectedVideoDeviceRef.current
					? {
							deviceId: { exact: selectedVideoDeviceRef.current },
							...(isMobile ? mobileDefaultConstraints : desktopDefaultConstraints),
						}
					: isMobile
						? mobileDefaultConstraints
						: desktopDefaultConstraints,
				audio: selectedAudioDeviceRef.current ? { deviceId: { exact: selectedAudioDeviceRef.current } } : true,
			};

			const newStream = await navigator.mediaDevices.getUserMedia(constraints);
			const videoTrack = newStream.getVideoTracks()[0];
			const audioTrack = newStream.getAudioTracks()[0];

			if (!videoTrack) {
				throw new Error('Failed to access video track');
			}

			if (!audioTrack) {
				throw new Error('Failed to access audio track');
			}

			// Save the original device IDs
			const originalVideoDeviceId = videoTrack?.getSettings().deviceId;
			const originalAudioDeviceId = audioTrack?.getSettings().deviceId;

			// Create the mirrored stream from the raw stream
			const mirroredStream = await createMirroredStream(newStream);

			// Store the original device IDs for future getUserMedia calls
			selectedVideoDeviceRef.current = originalVideoDeviceId;
			selectedAudioDeviceRef.current = originalAudioDeviceId;

			await getUserMediaDevices();
			setState((prev) => ({ ...prev, stream: mirroredStream, originalStream: newStream }));
			obtainedDevicePermissionRef.current = true;
		} catch (err) {
			console.error('MediaRecorder Error accessing media devices:', err);
			toast({
				title: (
					<Trans t={t} i18nKey="errors.device_permission">
						Device permissions have not been granted.
						<Link textDecoration="underline" href="https://help.bonus.ly/en/articles/11173301-enabling-camera-and-microphone-permissions" target="_blank">
							Learn more.
						</Link>
					</Trans>
				),
				status: 'error',
			});
		}
	}, [isMobile, selectedVideoDeviceRef, selectedAudioDeviceRef, toast, t]);

	// We've decided, per https://bonusly1612893911.atlassian.net/browse/BEAGLE-2595, to not
	// just show the user the mirrored stream, but capture the actual video in a mirrored state,
	// and use the mirrored version as the version that everyone sees. So we have to do a bit more work
	// to reverse the video stream here, then use it instead.
	const createMirroredStream = async (originalStream: MediaStream): Promise<MediaStream> => {
		return new Promise<MediaStream>((resolve, reject) => {
			// Create an offscreen video element to play the raw stream.
			const videoEl = document.createElement('video');
			videoEl.srcObject = originalStream;
			videoEl.muted = true;
			videoEl.play().catch((error) => {
				console.error('Error playing video:', error);
				reject(error);
			});

			videoEl.onloadedmetadata = () => {
				// Set up a canvas using the video element's dimensions.
				const canvas = document.createElement('canvas');
				canvas.width = videoEl.videoWidth;
				canvas.height = videoEl.videoHeight;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					reject(new Error('Unable to get canvas context'));
					return;
				}

				// Start a drawing loop that mirrors the video frame.
				let animationFrameId: number;
				const drawFrame = () => {
					ctx.save();
					// Move to the right edge and flip horizontally.
					ctx.translate(canvas.width, 0);
					ctx.scale(-1, 1);
					ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
					ctx.restore();
					animationFrameId = requestAnimationFrame(drawFrame);
				};
				drawFrame();

				// Capture the canvas as a MediaStream at 30 FPS (adjust if needed).
				const mirroredStream = canvas.captureStream(30);

				// Add the original audio track to the mirrored stream
				const audioTrack = originalStream.getAudioTracks()[0];
				if (audioTrack) {
					mirroredStream.addTrack(audioTrack);
				}

				// Attach a cleanup function to cancel the draw loop and stop the offscreen video element.
				(mirroredStream as any).cleanup = () => {
					cancelAnimationFrame(animationFrameId);
					videoEl.pause();
					if (videoEl.srcObject) {
						(videoEl.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
						videoEl.srcObject = null;
					}
				};

				resolve(mirroredStream);
			};
		});
	};

	const resetRecordingState = useCallback(() => {
		resetRecordedVideoState();
		clearTranscription();
		resetMediaRecorderState();
	}, []);

	const preloadCountdownAudio = useCallback(() => {
		[1, 2, 3, 4].forEach((number) => {
			const audio = new Audio(`${COUNTDOWN_BASE_URL}/countdown-${number}-4.mp3`);
			audio.load(); // Pre-load the audio file
			countdownAudioCache.current[number] = audio;
		});
	}, []);

	useEffect(() => {
		if (isOpen) {
			if (checkMediaRecorderSupport()) {
				track(TrackerVideoEvent.videoRecordingModalOpen, {});
				initializeMediaStream();
				preloadCountdownAudio();
			} else {
				toast({
					title: t('errors.unsupported_browser'),
					status: 'error',
				});
			}
		}

		return () => {
			stopRecording();
			resetRecordingState();
		};
	}, [isOpen, resetRecordingState, initializeMediaStream, preloadCountdownAudio]);

	useEffect(() => {
		const handleResize = async () => {
			if (isOpen && state.stream) {
				// If we're currently recording, stop the recording first
				if (state.isRecording) {
					// Set a flag to skip video processing since this is just a resize
					skipVideoProcessingRef.current = true;
					stopRecording();
				}
				await initializeMediaStream();
			}
		};

		// Call once immediately
		handleResize();

		// Create a debounced version for the resize observer
		let resizeTimeout: NodeJS.Timeout;
		const debouncedResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(handleResize, 150);
		};

		window.addEventListener('resize', debouncedResize);
		return () => {
			window.removeEventListener('resize', debouncedResize);
			clearTimeout(resizeTimeout);
		};
	}, [isMobile, isOpen]);

	// Safari won't play the recorded video on the preview
	// modal unless we load it.
	useEffect(() => {
		if (recordedVideoRef?.current) {
			recordedVideoRef.current.load();
		}
	}, [recordedVideoRef, state.recordedVideo, state.recordedPreviewReady]);

	const resetRecordedVideoState = () => {
		setState((prev) => ({
			...prev,
			recordedVideo: '',
			recordedPreviewReady: false,
			videoPoster: null,
		}));
		clearCountdown();
	};

	const resetMediaRecorderState = () => {
		// Call cleanup if it exists
		firefoxMediaRecorderCleanupRef.current?.();
		firefoxMediaRecorderCleanupRef.current = null;

		setState((prev) => ({
			...prev,
			isRecording: false,
			stream: null,
			originalStream: null,
			mediaRecorder: null,
		}));
		chunks.current = [];
		// We often re-initialize the stream. For example, if the
		// browser is resized, or if the user stops a recording,
		// then restarts it. In these cases, we need to release
		// our reference to the camera and microphone
		selectedVideoDeviceRef.current = undefined;
		selectedAudioDeviceRef.current = undefined;
	};

	const clearTranscription = () => {
		// Clear realtime transcription data
		if (mobileVideoFeatureFlag) {
			clearTranscriptText();
		}
	};

	const startRecording = () => {
		if (!state.stream) {
			initializeMediaStream();
		}
		setState((prev) => ({ ...prev, recordingAttempts: prev.recordingAttempts + 1 }));
		clearTranscription();
		track(TrackerVideoEvent.videoRecordStart, {});
		startCountdown(3, state.stream);
		return () => {
			clearCountdown();
		};
	};

	const startCountdown = (initialCountdown: number, stream: MediaStream | null) => {
		let currentCountdown = initialCountdown;
		const playCountdownSound = (number: number) => {
			const audio = countdownAudioCache.current[number];
			if (audio) {
				audio.currentTime = 0;
				audio.play().catch(console.error);
			}
		};

		setState((prev) => {
			playCountdownSound(4);
			return { ...prev, countdown: currentCountdown };
		});
		if (mobileVideoFeatureFlag) startTranscription();
		countdownIntervalRef.current = setInterval(() => {
			currentCountdown--;

			if (currentCountdown <= 0) {
				setState((prev) => {
					playCountdownSound(1);
					// Set countdown to empty string instead of null to prevent audio overlap/clipping
					// when transitioning from countdown to recording
					return { ...prev, countdown: '' };
				});
				setTimeout(() => {
					clearCountdown();
					startMediaRecording(stream);
				}, 500);
			} else {
				setState((prev) => {
					playCountdownSound(currentCountdown + 1);
					return { ...prev, countdown: currentCountdown };
				});
			}
		}, 1000);
	};

	const startMediaRecording = (stream: MediaStream | null) => {
		skipVideoProcessingRef.current = false; // User explicitly started recording so we don't skip video processing
		if (!stream || !stream.getTracks().length || stream.getTracks().some((track) => track.readyState !== 'live')) {
			console.error('MediaRecorder: No media tracks available or stream is not active', stream);
			resetRecordingState();
			return;
		}

		// Clear chunks right before starting new recording
		chunks.current = [];

		// Modify the options to ensure better compatibility
		const options = {
			...getSupportedMimeType(),
			videoBitsPerSecond: 2500000, // 2.5 Mbps
			audioBitsPerSecond: 128000, // 128 kbps
		};

		const recorder = new MediaRecorder(stream, options);

		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) {
				chunks.current.push(event.data);
			}
		};

		const videoTrack = stream.getVideoTracks()[0];
		const audioTrack = stream.getAudioTracks()[0];

		// Store the original device IDs for future getUserMedia calls
		selectedVideoDeviceRef.current = videoTrack?.getSettings().deviceId;
		selectedAudioDeviceRef.current = audioTrack?.getSettings().deviceId;

		recorder.onstop = async () => {
			await handleRecordingStop(recorder, videoTrack, audioTrack);
		};

		recorder.onerror = async (event) => {
			console.error('MediaRecorder: error:', event);
			resetRecordingState();

			toast({
				title: t('errors.recording_failed', { code: 'E005' }),
				status: 'error',
			});
		};

		try {
			recorder.start(1000);
			setState((prev) => ({ ...prev, mediaRecorder: recorder, isRecording: true }));
		} catch (error) {
			console.error('Failed to start MediaRecorder:', error);
			resetRecordingState();
			toast({
				title: t('errors.recording_failed', { code: 'E008' }),
				status: 'error',
			});
		}
	};

	const createVideoFromBlob = async (blob: Blob, mimeType: string) => {
		try {
			const videoUrl = URL.createObjectURL(blob);
			setState((prev) => ({
				...prev,
				recordedVideo: videoUrl,
			}));

			const file = new File([blob], `recorded-video.${getExtensionFromRecorderMimeType()}`, { type: mimeType });
			await saveFileToBrowserStorage(file);
			await createVideoPoster(file);

			// Clean up old video URL if it exists
			if (state.recordedVideo) {
				URL.revokeObjectURL(state.recordedVideo);
			}

			// Avoids a flash of content
			setState((prev) => ({ ...prev, recordedPreviewReady: true }));
		} catch (error) {
			console.error('Error creating video from blob:', error);
			resetRecordingState();
			toast({
				title: t('errors.recording_failed', { code: 'E006' }),
				status: 'error',
			});
		}
	};

	const handleRecordingStop = async (recorder: MediaRecorder, videoTrack: MediaStreamTrack, audioTrack: MediaStreamTrack) => {
		if (skipVideoProcessingRef.current === true) {
			skipVideoProcessingRef.current = false;
			return;
		}

		if (selectedVideoDeviceRef.current !== videoTrack?.getSettings().deviceId || selectedAudioDeviceRef.current !== audioTrack?.getSettings().deviceId) {
			console.error('MediaRecorder: No device change');
			resetRecordingState();
			return;
		}

		if (chunks.current.length === 0) {
			console.error('MediaRecorder: No video data chunks recorded');
			resetRecordingState();
			toast({
				title: t('errors.recording_failed', { code: 'E001' }),
				status: 'error',
			});
			return;
		}

		const mimeType = recorder.mimeType || 'video/webm';

		if (!MediaRecorder.isTypeSupported(mimeType)) {
			console.error('MediaRecorder: Unsupported mime type:', mimeType);
			resetRecordingState();
			toast({
				title: t('errors.recording_failed', { code: 'E007' }),
				status: 'error',
			});
			return;
		}

		const blob = new Blob(chunks.current, { type: mimeType });

		if (blob.size === 0) {
			console.error('MediaRecorder: Created blob has zero size');
			resetRecordingState();

			toast({
				title: t('errors.recording_failed', { code: 'E002' }),
				status: 'error',
			});
			return;
		}

		try {
			await validateVideo(blob);
			await createVideoFromBlob(blob, mimeType);
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			console.error('Detailed error:', errorMessage);
			toast({
				title: t('errors.recording_failed', { code: 'E003' }),
				status: 'error',
			});
		} finally {
			resetMediaRecorderState();
		}
	};

	const createVideoPoster = async (file: File) => {
		const poster = await generatePoster(file);
		if (poster.posterFile) {
			await saveFileToBrowserStorage(poster.posterFile, 'poster');
			setState((prev) => ({ ...prev, videoPoster: poster.posterUrl }));
		}
	};

	const cleanupStream = (stream: MediaStream | null) => {
		if (stream) {
			if ((stream as any).cleanup && typeof (stream as any).cleanup === 'function') {
				(stream as any).cleanup();
			}

			stream.getTracks().forEach((track) => {
				track.stop();
				track.enabled = false;
			});
		}
	};

	// We store our streams in the state object so that consuming compnents
	// can control their lifecycles with them. Setting state is asynchronous,
	// so we need to use a cleanup function to stop the streams when state is updated.
	useEffect(() => {
		if (previousStreamRef.current && state.stream?.id !== previousStreamRef.current.id) {
			cleanupStream(previousStreamRef.current);
		}
		previousStreamRef.current = state.stream;

		if (previousOriginalStreamRef.current && state.originalStream?.id !== previousOriginalStreamRef.current.id) {
			cleanupStream(previousOriginalStreamRef.current);
		}
		previousOriginalStreamRef.current = state.originalStream;
	}, [state.stream, state.originalStream]);

	const downloadVideo = () => {
		if (state.recordedVideo) {
			const a = document.createElement('a');
			a.href = state.recordedVideo;
			a.download = getVideoFilename();
			a.click();
		}
	};

	const handleDeviceChange = async (deviceId: string, type: 'video' | 'audio') => {
		const selectedRef = type === 'video' ? selectedVideoDeviceRef : selectedAudioDeviceRef;
		if (deviceId !== selectedRef.current) {
			selectedRef.current = deviceId;
			stopRecording();
			saveDevicePreferences(type === 'video' ? deviceId : undefined, type === 'audio' ? deviceId : undefined);
			await initializeMediaStream();
		}
	};

	const getVideoFilename = () => `video.${getExtensionFromRecorderMimeType()}`;
	const restartRecording = async () => {
		// Safely handle video playback
		if (recordedVideoRef?.current) {
			recordedVideoRef.current.pause();
			recordedVideoRef.current.currentTime = 0;
		}

		// Stop transcription before resetting
		if (mobileVideoFeatureFlag && isTranscribing) stopTranscription();

		// Reset state
		skipVideoProcessingRef.current = true;
		stopRecording();
		resetMediaRecorderState();
		clearTranscription();
		// Reinitialize stream
		await initializeMediaStream();

		await deleteFileFromBrowserStorage();
		// FIXME: I believe there's a race condition between initializeMediaStream's state updates and resetRecordedVideoState
		// The await appwars to have some async behavior or we set state multiple timesthat causes video flashing, but we need to implement a proper ready state check
		// We implmeneted setTimeout here but it's not a great solution and still causes a flash of the recorded video when restarting
		setTimeout(() => resetRecordedVideoState(), 100);
	};

	return {
		...state,
		videoStream: state.stream,
		isLoadingSuggestedReason,
		// Refs
		videoRef,
		obtainedDevicePermissionRef,
		selectedVideoDeviceRef,
		selectedAudioDeviceRef,
		// Actions
		startRecording,
		resetRecordingState,
		stopRecording,
		restartRecording,
		downloadVideo,
		handleDeviceChange,
		getVideoFilename,
		getSuggestedBonusReason,
		getSpeechTags,
	};
};
